import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getVacancies,
  getVacancy,
  createVacancy,
  updateVacancy,
  deleteVacancy,
  duplicateVacancy,
  archiveVacancy,
  restoreVacancy,
  updateVacancyStatus,
  reorderCandidates,
  convertCandidateToEmployee,
  type ConvertCandidateToEmployeePayload,
  type ReorderCandidateItem,
  getVacancyPipeline,
  getRejectionReasons,
  getAllRejectionReasons,
  createRejectionReason,
  updateRejectionReason,
  bulkAssignCandidatesToVacancy,
  bulkAssignCandidatesMulti,
  searchSkills,
  listSkillsAdmin,
  getSkillAdmin,
  createSkillAdmin,
  renameSkillAdmin,
  deleteSkillAdmin,
  addSkillAlias,
  removeSkillAlias,
  previewSkillMerge,
  mergeSkills,
  getPipelineTemplates,
  getPipelineTemplate,
  createPipelineTemplate,
  updatePipelineTemplate,
  deletePipelineTemplate,
  addTemplateStage,
  updateTemplateStage,
  deleteTemplateStage,
  reorderTemplateStages,
  addPipelineStage,
  updatePipelineStage,
  deletePipelineStage,
  reorderPipelineStages,
} from '@/lib/recruitment';
import { candidateKeys } from '@/hooks/use-candidates';
import { employeesKeys } from '@/hooks/use-employees';
import type {
  VacancyQueryParams,
  VacancyDetail,
  VacancyStatus,
  CreateVacancyInput,
  UpdateVacancyInput,
  AddPipelineStageInput,
  UpdatePipelineStageInput,
  ReorderPipelineStagesInput,
  CreatePipelineTemplateInput,
  UpdatePipelineTemplateInput,
  CreateRejectionReasonInput,
  UpdateRejectionReasonInput,
  BulkAssignCandidatesInput,
  BulkAssignCandidatesMultiInput,
} from '@/types/recruitment';

const STALE_TIME = 30_000; // 30s

export const recruitmentKeys = {
  all: ['recruitment'] as const,
  vacancies: () => [...recruitmentKeys.all, 'vacancies'] as const,
  vacancyList: (params: VacancyQueryParams) => [...recruitmentKeys.vacancies(), 'list', params] as const,
  vacancyDetail: (id: string) => [...recruitmentKeys.vacancies(), 'detail', id] as const,
  skills: (search: string) => [...recruitmentKeys.all, 'skills', search] as const,
  pipelineTemplates: () => [...recruitmentKeys.all, 'pipeline-templates'] as const,
  pipelineTemplate: (id: string) => [...recruitmentKeys.pipelineTemplates(), id] as const,
  rejectionReasons: () => [...recruitmentKeys.all, 'rejection-reasons'] as const,
  rejectionReasonsAll: () => [...recruitmentKeys.rejectionReasons(), 'all'] as const,
  skillsAdmin: () => [...recruitmentKeys.all, 'skills-admin'] as const,
  skillsAdminList: (search: string) => [...recruitmentKeys.skillsAdmin(), 'list', search] as const,
  skillAdminDetail: (id: string) => [...recruitmentKeys.skillsAdmin(), 'detail', id] as const,
};

export function useVacancies(params: VacancyQueryParams = {}) {
  return useQuery({
    queryKey: recruitmentKeys.vacancyList(params),
    queryFn: () => getVacancies(params),
    staleTime: STALE_TIME,
  });
}

export function useVacancy(id: string) {
  return useQuery({
    queryKey: recruitmentKeys.vacancyDetail(id),
    queryFn: () => getVacancy(id),
    staleTime: STALE_TIME,
    enabled: !!id,
  });
}

export function useCreateVacancy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVacancyInput) => createVacancy(input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
    },
  });
}

// `suppressGlobalToast` lets callers that already surface their own error
// (e.g. an autosave status pill) opt out of the global mutation-error toast;
// callers with no handling of their own (the common case) omit it and get
// the global fallback.
export function useUpdateVacancy(opts?: { suppressGlobalToast?: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateVacancyInput & { id: string }) =>
      updateVacancy(id, input),
    meta: { suppressGlobalToast: opts?.suppressGlobalToast ?? false },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(variables.id) });
    },
  });
}

export function useDeleteVacancy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVacancy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
    },
  });
}

export function useDuplicateVacancy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => duplicateVacancy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
    },
  });
}

export function useArchiveVacancy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveVacancy(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(id) });
    },
  });
}

export function useRestoreVacancy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreVacancy(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(id) });
    },
  });
}

export function useUpdateVacancyStatus(vacancyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: VacancyStatus) => updateVacancyStatus(vacancyId, status),
    // Rollback-only onError; call sites supply their own toast. Explicit flag for consistency.
    meta: { suppressGlobalToast: true },
    onMutate: async (status) => {
      await queryClient.cancelQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
      const previous = queryClient.getQueryData<VacancyDetail>(
        recruitmentKeys.vacancyDetail(vacancyId),
      );
      if (previous) {
        queryClient.setQueryData<VacancyDetail>(
          recruitmentKeys.vacancyDetail(vacancyId),
          { ...previous, status },
        );
      }
      return { previous };
    },
    onError: (_err, _status, context) => {
      if (context?.previous) {
        queryClient.setQueryData(recruitmentKeys.vacancyDetail(vacancyId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
    },
  });
}

export function useVacancyPipeline(vacancyId: string) {
  return useQuery({
    queryKey: [...recruitmentKeys.vacancyDetail(vacancyId), 'pipeline'] as const,
    queryFn: () => getVacancyPipeline(vacancyId),
    staleTime: STALE_TIME,
    enabled: !!vacancyId,
  });
}

export function useBulkAssignCandidates(vacancyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkAssignCandidatesInput) =>
      bulkAssignCandidatesToVacancy(vacancyId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
}

export function useBulkAssignCandidatesMulti() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkAssignCandidatesMultiInput) => bulkAssignCandidatesMulti(input),
    meta: { suppressGlobalToast: true },
    onSuccess: (_data, variables) => {
      for (const vacancyId of variables.vacancyIds) {
        queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
      }
      for (const candidateId of variables.candidateIds) {
        queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
        queryClient.invalidateQueries({ queryKey: candidateKeys.assignments(candidateId) });
      }
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
    },
  });
}

export function useReorderCandidates(vacancyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: ReorderCandidateItem[]) => reorderCandidates(vacancyId, items),
    meta: { suppressGlobalToast: true },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
      // HW-459: kanban DnD across columns goes through reorder; cross-column moves
      // can flip Candidate.status server-side (ACTIVE↔HIRING). Items carry assignment
      // IDs, not candidate IDs, so invalidate the broader candidate cache — TanStack
      // Query only refetches actively observed entries (e.g. the open profile header).
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
    },
  });
}

// HW-480: hire flow — opens AddEmployeeDialog from DnD into a HIRED-mapped column,
// then creates the Employee + flips Candidate.status atomically on the server.
export function useConvertCandidateToEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      candidateId,
      payload,
    }: {
      candidateId: string;
      payload: ConvertCandidateToEmployeePayload;
    }) => convertCandidateToEmployee(candidateId, payload),
    meta: { suppressGlobalToast: true },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: recruitmentKeys.vacancyDetail(variables.payload.vacancyId),
      });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(variables.candidateId) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

// HW-494: rejecting / restoring a candidate is now a kanban drag (into or out of an
// ARCHIVED-mapped stage) and goes through useReorderCandidates — there is no longer a
// dedicated reject / restore mutation.

// Rejection reasons (HW-494)
export function useRejectionReasons() {
  return useQuery({
    queryKey: recruitmentKeys.rejectionReasons(),
    queryFn: getRejectionReasons,
    staleTime: 5 * 60_000, // 5 min — reasons change rarely
  });
}

export function useAllRejectionReasons() {
  return useQuery({
    queryKey: recruitmentKeys.rejectionReasonsAll(),
    queryFn: getAllRejectionReasons,
    staleTime: 5 * 60_000,
  });
}

function invalidateRejectionReasons(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: recruitmentKeys.rejectionReasons() });
}

export function useCreateRejectionReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRejectionReasonInput) => createRejectionReason(input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateRejectionReasons(queryClient),
  });
}

// Backs both the rename form (has its own toast) and the Active/Disabled
// toggle (no handling of its own) — see useUpdateVacancy above for why this
// takes an opt-in flag instead of a fixed meta.
export function useUpdateRejectionReason(opts?: { suppressGlobalToast?: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateRejectionReasonInput & { id: string }) =>
      updateRejectionReason(id, input),
    meta: { suppressGlobalToast: opts?.suppressGlobalToast ?? false },
    onSuccess: () => invalidateRejectionReasons(queryClient),
  });
}

export function useSearchSkills(search: string) {
  return useQuery({
    queryKey: recruitmentKeys.skills(search),
    queryFn: () => searchSkills(search),
    staleTime: STALE_TIME,
    enabled: search.length >= 2,
  });
}

// Skills admin (HW-530)

function invalidateSkillsAdmin(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: recruitmentKeys.skillsAdmin() });
}

export function useSkillsAdminList(search: string) {
  return useQuery({
    queryKey: recruitmentKeys.skillsAdminList(search),
    queryFn: () => listSkillsAdmin(search || undefined),
    staleTime: STALE_TIME,
  });
}

export function useSkillAdminDetail(id: string | null) {
  return useQuery({
    queryKey: recruitmentKeys.skillAdminDetail(id ?? ''),
    queryFn: () => getSkillAdmin(id as string),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

export function useCreateSkillAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createSkillAdmin(name),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateSkillsAdmin(queryClient),
  });
}

export function useRenameSkillAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameSkillAdmin(id, name),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateSkillsAdmin(queryClient),
  });
}

export function useDeleteSkillAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSkillAdmin(id),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateSkillsAdmin(queryClient),
  });
}

export function useAddSkillAlias(skillId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alias: string) => addSkillAlias(skillId, alias),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateSkillsAdmin(queryClient),
  });
}

export function useRemoveSkillAlias(skillId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (aliasId: string) => removeSkillAlias(skillId, aliasId),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateSkillsAdmin(queryClient),
  });
}

export function useMergeSkills() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loserId, winnerId }: { loserId: string; winnerId: string }) =>
      mergeSkills(loserId, winnerId),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      invalidateSkillsAdmin(queryClient);
      // Merge repoints candidate/vacancy skill rows in bulk — invalidate so any
      // open profile/list view picks up the collapsed chips on next render.
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancies() });
    },
  });
}

export { previewSkillMerge };

export function usePipelineTemplates() {
  return useQuery({
    queryKey: recruitmentKeys.pipelineTemplates(),
    queryFn: getPipelineTemplates,
    staleTime: 5 * 60_000, // 5 min — templates change rarely
  });
}

// Pipeline Stage Mutations

export function useAddPipelineStage(vacancyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddPipelineStageInput) => addPipelineStage(vacancyId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
    },
  });
}

export function useUpdatePipelineStage(vacancyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stageId, ...input }: UpdatePipelineStageInput & { stageId: string }) =>
      updatePipelineStage(vacancyId, stageId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
    },
  });
}

export function useDeletePipelineStage(vacancyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => deletePipelineStage(vacancyId, stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
    },
  });
}

export function useReorderPipelineStages(vacancyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReorderPipelineStagesInput) => reorderPipelineStages(vacancyId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.vacancyDetail(vacancyId) });
    },
  });
}

// Pipeline Template Mutations

export function usePipelineTemplate(id: string | null) {
  return useQuery({
    queryKey: recruitmentKeys.pipelineTemplate(id ?? ''),
    queryFn: () => getPipelineTemplate(id!),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

function invalidateTemplates(queryClient: ReturnType<typeof useQueryClient>, templateId?: string) {
  queryClient.invalidateQueries({ queryKey: recruitmentKeys.pipelineTemplates() });
  if (templateId) {
    queryClient.invalidateQueries({ queryKey: recruitmentKeys.pipelineTemplate(templateId) });
  }
}

export function useCreatePipelineTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePipelineTemplateInput) => createPipelineTemplate(input),
    onSuccess: () => invalidateTemplates(queryClient),
  });
}

export function useUpdatePipelineTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdatePipelineTemplateInput & { id: string }) =>
      updatePipelineTemplate(id, input),
    onSuccess: (_data, variables) => invalidateTemplates(queryClient, variables.id),
  });
}

export function useDeletePipelineTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePipelineTemplate(id),
    onSuccess: () => invalidateTemplates(queryClient),
  });
}

export function useAddTemplateStage(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddPipelineStageInput) => addTemplateStage(templateId, input),
    onSuccess: () => invalidateTemplates(queryClient, templateId),
  });
}

export function useUpdateTemplateStage(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stageId, ...input }: UpdatePipelineStageInput & { stageId: string }) =>
      updateTemplateStage(templateId, stageId, input),
    onSuccess: () => invalidateTemplates(queryClient, templateId),
  });
}

export function useDeleteTemplateStage(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => deleteTemplateStage(templateId, stageId),
    onSuccess: () => invalidateTemplates(queryClient, templateId),
  });
}

export function useReorderTemplateStages(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReorderPipelineStagesInput) => reorderTemplateStages(templateId, input),
    onSuccess: () => invalidateTemplates(queryClient, templateId),
  });
}
